import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState, LoadingSkeleton } from "@/components/shared/States";
import { ScorePill } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Search } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

export default function Companies() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", domain: "", industry: "", revenue: "", employees: "" });

  const load = async (q = "") => { setItems(null); const { data } = await api.get(`/companies?limit=100${q ? `&search=${encodeURIComponent(q)}` : ""}`); setItems(data.items); setTotal(data.total); };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);
  const create = async () => { if (!form.name) return toast.error("Name required"); await api.post("/companies", form); toast.success("Company created"); setOpen(false); setForm({ name: "", domain: "", industry: "", revenue: "", employees: "" }); load(); };

  return (
    <div>
      <PageHeader title="Companies" subtitle={`${total} accounts`} icon={Building2}
        actions={<Button data-testid="new-company-button" className="aurelo-gradient text-white" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Company</Button>} />
      <div className="mb-4 relative max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search companies…" className="pl-9" /></div>
      {items === null ? <LoadingSkeleton /> : items.length === 0 ? (
        <EmptyState icon={Building2} title="No companies yet" description="Create accounts to track deals, contacts and account intelligence." actionLabel="New Company" action={() => setOpen(true)} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((c) => (
            <div key={c.id} data-testid="company-card" onClick={() => navigate(`/app/companies/${c.id}`)} className="rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-[0_8px_24px_rgba(11,16,43,0.1)] cursor-pointer transition-shadow">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl aurelo-gradient text-white flex items-center justify-center font-bold">{c.name[0]}</div>
                <div className="min-w-0"><div className="font-semibold text-slate-900 truncate">{c.name}</div><div className="text-xs text-slate-400">{c.domain}</div></div>
                <div className="ml-auto"><ScorePill score={c.account_score} /></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><div className="text-[11px] text-slate-400">Industry</div><div className="text-xs font-medium truncate">{c.industry || "—"}</div></div>
                <div><div className="text-[11px] text-slate-400">Employees</div><div className="text-xs font-medium">{c.employees || "—"}</div></div>
                <div><div className="text-[11px] text-slate-400">Revenue</div><div className="text-xs font-medium">{c.revenue || "—"}</div></div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Company</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Company name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Domain</Label><Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="mt-1" /></div>
            <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="mt-1" /></div>
            <div><Label>Revenue</Label><Input value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} className="mt-1" placeholder="$100M" /></div>
            <div><Label>Employees</Label><Input value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="aurelo-gradient text-white" onClick={create}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
